import { Shell } from "../../components/Shell";
import { ActivityBars } from "../../components/ActivityBars";
import { AuditTable } from "../../components/AuditTable";
import { EnvBadge, ErrorNotice, Loading, Money, PageHead, Panel } from "../../components/ui";
import { adminApi } from "../../lib/api";
import { formatCount } from "../../lib/money";
import { useQuery } from "../../lib/query";
import { Link } from "../../lib/router";
import { useClient } from "../../lib/session";
import { relativeTime } from "../../lib/time";
import type { EnvCount } from "../../lib/types";

function Figure({ label, value, split }: { label: string; value: number; split?: EnvCount }) {
  return (
    <div className="figure">
      <div className="label">{label}</div>
      <div className="value">{formatCount(value)}</div>
      {split && (
        <div className="split">
          {formatCount(split.sandbox)} sandbox, {formatCount(split.production)} live
        </div>
      )}
    </div>
  );
}

const sum = (c: EnvCount) => c.sandbox + c.production;

export function AdminOverview() {
  const api = adminApi(useClient());
  const ov = useQuery("admin:overview", () => api.overview(), { refreshMs: 30_000 });
  const audit = useQuery("admin:audit:recent", () => api.auditLogs({ limit: 8 }));
  const o = ov.data;

  return (
    <Shell crumbs={[{ label: "Overview" }]}>
      <PageHead
        title="Platform overview"
        lede={o ? `Updated ${relativeTime(o.generated_at)}. Figures cover every developer and game on this engine.` : undefined}
      />
      <ErrorNotice error={ov.error} onRetry={ov.reload} />
      {!o && ov.loading && <Loading />}
      {o && (
        <div className="stack">
          <div className="figure-row">
            <Figure label="Developers" value={o.developers.total} />
            <Figure label="Games" value={o.games.total} />
            <Figure label="Player accounts" value={sum(o.player_accounts)} split={o.player_accounts} />
            <Figure label="Transactions, last 24 h" value={sum(o.transactions_24h)} split={o.transactions_24h} />
            <Figure label="Active holds" value={sum(o.active_holds)} split={o.active_holds} />
          </div>

          <div className="grid-2">
            <Panel title="Money in circulation" sub="What each environment's banks have issued, less anything reversed." flush>
              {o.circulation.length === 0 ? (
                <p className="muted panel-body">No bank has issued money yet.</p>
              ) : (
                <table className="circulation">
                  <tbody>
                    {o.circulation.map((c) => (
                      <tr key={c.environment + c.currency}>
                        <td>
                          <div className="big">
                            <Money minor={c.issued_minor} currency={{ code: c.currency, exponent: c.exponent, symbol: c.symbol }} />
                          </div>
                          <div className="muted small">
                            {c.currency}
                            {c.custom ? " (custom unit)" : ""} across {c.banks} bank{c.banks === 1 ? "" : "s"}
                          </div>
                        </td>
                        <td className="num" style={{ textAlign: "right" }}>
                          <EnvBadge env={c.environment} />
                          <div className="muted small" style={{ marginTop: 6 }}>
                            Transferred today:{" "}
                            <Money minor={c.transfer_volume_24h} currency={{ code: c.currency, exponent: c.exponent, symbol: c.symbol }} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>
            <Panel title="Ledger activity" sub="Posted transactions per day, last 14 days.">
              <ActivityBars days={o.activity_14d} />
            </Panel>
          </div>

          <div className="grid-3">
            <Panel title="Tenants">
              <dl className="facts">
                <dt>Active developers</dt>
                <dd>{o.developers.active}</dd>
                <dt>Suspended developers</dt>
                <dd>{o.developers.suspended}</dd>
                <dt>Active games</dt>
                <dd>{o.games.active}</dd>
                <dt>Suspended games</dt>
                <dd>{o.games.suspended}</dd>
              </dl>
            </Panel>
            <Panel title="Infrastructure">
              <dl className="facts">
                <dt>Banks</dt>
                <dd>{o.banks.sandbox} sandbox, {o.banks.production} live</dd>
                <dt>Active API keys</dt>
                <dd>{o.active_api_keys.sandbox} sandbox, {o.active_api_keys.production} live</dd>
                <dt>Players</dt>
                <dd>{o.players.sandbox} sandbox, {o.players.production} live</dd>
                <dt>All-time transactions</dt>
                <dd>{formatCount(sum(o.transactions))}</dd>
              </dl>
            </Panel>
            <Panel title="Shortcuts">
              <div className="stack" style={{ gap: 10 }}>
                <Link className="btn" to="/admin/developers?new=1">Register a developer</Link>
                <Link className="btn" to="/admin/integrity">Check ledger health</Link>
                <Link className="btn" to="/admin/audit?action=funding.">Review funding activity</Link>
              </div>
            </Panel>
          </div>

          <Panel title="Recent platform activity" actions={<Link to="/admin/audit">Full audit log</Link>} flush>
            <ErrorNotice error={audit.error} />
            {audit.data && <AuditTable rows={audit.data.data} compact />}
          </Panel>
        </div>
      )}
    </Shell>
  );
}
